import React, { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  LayoutDashboard, CalendarDays, Image, HelpCircle, MessageSquare, 
  Settings, LogOut, Menu, X, Home, Users, IndianRupee, Package, Percent, Tag
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("adminToken")}` }
});

// Dashboard Component
const DashboardHome = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/admin/stats`, getAuthHeaders());
        setStats(res.data);
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) return <div className="spinner mx-auto mt-20" />;

  const statCards = [
    { label: "Total Bookings", value: stats?.total_bookings || 0, icon: CalendarDays, color: "bg-blue-500" },
    { label: "Confirmed", value: stats?.confirmed_bookings || 0, icon: Users, color: "bg-green-500" },
    { label: "Pending", value: stats?.pending_bookings || 0, icon: Package, color: "bg-yellow-500" },
    { label: "Revenue", value: `₹${(stats?.total_revenue || 0).toLocaleString()}`, icon: IndianRupee, color: "bg-purple-500" },
    { label: "Unread Messages", value: stats?.unread_messages || 0, icon: MessageSquare, color: "bg-red-500" },
    { label: "Active Rooms", value: stats?.total_rooms || 0, icon: Home, color: "bg-teal-500" },
  ];

  return (
    <div data-testid="admin-dashboard-home">
      <h1 className="font-serif text-3xl font-bold text-[#2C3E50] mb-8">Dashboard</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => (
          <div key={index} className="bg-white rounded-xl p-6 shadow-sm" data-testid={`stat-card-${index}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[#7F8C8D] text-sm">{stat.label}</p>
                <p className="text-2xl font-bold text-[#2C3E50] mt-1">{stat.value}</p>
              </div>
              <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                <stat.icon className="text-white" size={24} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Bookings Management
const BookingsManagement = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Format date to DD-MM-YYYY
  const formatDateDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Format datetime to DD-MM-YYYY HH:MM AM/PM
  const formatDateTimeDisplay = (dateString) => {
    if (!dateString) return { date: '', time: '' };
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return {
      date: `${day}-${month}-${year}`,
      time: `${hours12}:${minutes} ${ampm}`
    };
  };

  // Generate booking number from UUID as fallback (if not stored in DB)
  const generateBookingNumber = (id) => {
    if (!id) return '#00000';
    // Take first 8 characters of UUID and convert from hex to decimal
    const hexPart = id.replace(/-/g, '').substring(0, 8);
    const numericValue = parseInt(hexPart, 16);
    // Use modulo to get a number between 1-99999, then pad with zeros
    const bookingNum = (numericValue % 99999) + 1;
    return `#${String(bookingNum).padStart(5, '0')}`;
  };

  // Get booking number - prefer DB stored value, fallback to generated
  const getBookingNumber = (booking) => {
    return booking.booking_number || generateBookingNumber(booking.id);
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const res = await axios.get(`${API}/admin/bookings`, getAuthHeaders());
      setBookings(res.data);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/status?status=${status}`, {}, getAuthHeaders());
      toast.success("Status updated");
      fetchBookings();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  if (loading) return <div className="spinner mx-auto mt-20" />;

  return (
    <div data-testid="bookings-management">
      <h1 className="font-serif text-3xl font-bold text-[#2C3E50] mb-8">Bookings</h1>
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Guest</TableHead>
              <TableHead>Booking No</TableHead>
              <TableHead>Booking Date</TableHead>
              <TableHead>Check-in</TableHead>
              <TableHead>Check-out</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Payment</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bookings.map((booking) => (
              <TableRow key={booking.id}>
                <TableCell>
                  <div>
                    <p className="font-medium">{booking.guest_name}</p>
                    <p className="text-sm text-gray-500">{booking.guest_email}</p>
                  </div>
                </TableCell>
                <TableCell className="font-mono text-sm font-semibold">{getBookingNumber(booking)}</TableCell>
                <TableCell className="text-sm">
                  <div>
                    <div className="font-medium">{formatDateTimeDisplay(booking.created_at).date}</div>
                    <div className="text-xs text-gray-500">{formatDateTimeDisplay(booking.created_at).time}</div>
                  </div>
                </TableCell>
                <TableCell>{formatDateDisplay(booking.check_in_date)}</TableCell>
                <TableCell>{formatDateDisplay(booking.check_out_date)}</TableCell>
                <TableCell>₹{booking.total_amount?.toLocaleString()}</TableCell>
                <TableCell>
                  <span className={`px-2 py-1 rounded text-xs ${
                    booking.payment_status === "paid" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"
                  }`}>
                    {booking.payment_status}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={`px-2 py-1 rounded text-xs ${
                    booking.booking_status === "confirmed" ? "bg-green-100 text-green-800" : 
                    booking.booking_status === "cancelled" ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800"
                  }`}>
                    {booking.booking_status}
                  </span>
                </TableCell>
                <TableCell>
                  <Select onValueChange={(value) => updateStatus(booking.id, value)}>
                    <SelectTrigger className="w-32">
                      <SelectValue placeholder="Update" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="confirmed">Confirm</SelectItem>
                      <SelectItem value="cancelled">Cancel</SelectItem>
                      <SelectItem value="completed">Complete</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {bookings.length === 0 && (
          <p className="text-center py-8 text-gray-500">No bookings yet</p>
        )}
      </div>
    </div>
  );
};

// Rooms Management
const RoomsManagement = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRoom, setEditingRoom] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "", 
    description: "", 
    price_per_night: "", 
    default_price_per_night: "",
    default_price_per_adult: "",
    default_price_per_children: "",
    seasonal_pricing: [],
    blocked_dates: [],
    max_guests: 4, 
    amenities: [], 
    images: []
  });

  // Helper function to convert ISO date to YYYY-MM-DD format
  const formatDateForInput = (isoDate) => {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    return date.toISOString().split('T')[0];
  };

  // Helper function to prepare room data for editing
  const prepareRoomDataForEdit = (room) => {
    return {
      ...room,
      seasonal_pricing: room.seasonal_pricing?.map(sp => ({
        ...sp,
        from_date: formatDateForInput(sp.from_date),
        to_date: formatDateForInput(sp.to_date)
      })) || [],
      blocked_dates: room.blocked_dates?.map(bd => ({
        ...bd,
        from_date: formatDateForInput(bd.from_date || bd.date),
        to_date: bd.to_date ? formatDateForInput(bd.to_date) : ''
      })) || []
    };
  };

  // Get today's date in YYYY-MM-DD format for min attribute
  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Format date from YYYY-MM-DD to DD-MM-YYYY for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '(select date)';
    const [year, month, day] = dateString.split('-');
    return `${day}-${month}-${year}`;
  };

  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    try {
      const res = await axios.get(`${API}/admin/rooms`, getAuthHeaders());
      setRooms(res.data);
    } catch (error) {
      toast.error("Failed to load rooms");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { 
        ...formData, 
        price_per_night: parseFloat(formData.price_per_night),
        default_price_per_night: parseFloat(formData.default_price_per_night || formData.price_per_night),
        default_price_per_adult: parseFloat(formData.default_price_per_adult || 0),
        default_price_per_children: parseFloat(formData.default_price_per_children || 0),
        max_guests: parseInt(formData.max_guests)
      };
      if (editingRoom) {
        await axios.put(`${API}/admin/rooms/${editingRoom.id}`, data, getAuthHeaders());
        toast.success("Room updated");
      } else {
        await axios.post(`${API}/admin/rooms`, data, getAuthHeaders());
        toast.success("Room created");
      }
      setShowForm(false);
      setEditingRoom(null);
      setFormData({ 
        name: "", 
        description: "", 
        price_per_night: "", 
        default_price_per_night: "",
        default_price_per_adult: "",
        default_price_per_children: "",
        seasonal_pricing: [],
        blocked_dates: [],
        max_guests: 4, 
        amenities: [], 
        images: [] 
      });
      fetchRooms();
    } catch (error) {
      toast.error("Failed to save room");
    }
  };

  const deleteRoom = async (id) => {
    if (!window.confirm("Delete this room?")) return;
    try {
      await axios.delete(`${API}/admin/rooms/${id}`, getAuthHeaders());
      toast.success("Room deleted");
      fetchRooms();
    } catch (error) {
      toast.error("Failed to delete room");
    }
  };

  if (loading) return <div className="spinner mx-auto mt-20" />;

  return (
    <div data-testid="rooms-management">
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-serif text-3xl font-bold text-[#2C3E50]">Rooms</h1>
        <Button onClick={() => { setShowForm(true); setEditingRoom(null); }} className="btn-primary" data-testid="add-room-btn">
          Add Room
        </Button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8" data-testid="room-form">
          <h2 className="font-semibold text-lg mb-4">{editingRoom ? "Edit Room" : "Add New Room"}</h2>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Room Name</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
              </div>
              {/* <div>
                <Label>Price per Night (₹) - Legacy</Label>
                <Input type="number" value={formData.price_per_night} onChange={(e) => setFormData({...formData, price_per_night: e.target.value})} required />
              </div> */}
                <div>
              <Label>Base Guests</Label>
              <Input type="number" value={formData.max_guests} onChange={(e) => setFormData({...formData, max_guests: e.target.value})} />
            </div>
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} />
            </div>
            
            {/* <div>
              <Label>Base Guests</Label>
              <Input type="number" value={formData.max_guests} onChange={(e) => setFormData({...formData, max_guests: e.target.value})} />
            </div> */}
            
            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-md text-[#2C3E50]">Blocked Dates (Disable Booking)</h3>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setFormData({
                      ...formData, 
                      blocked_dates: [
                        ...formData.blocked_dates, 
                        { from_date: '', to_date: '', reason: 'maintenance' }
                      ]
                    });
                  }}
                >
                  Add Blocked Period
                </Button>
              </div>
              
              {formData.blocked_dates && formData.blocked_dates.length > 0 && (
                <div className="space-y-3">
                  {formData.blocked_dates.map((blocked, index) => (
                    <div key={index} className="bg-red-50 p-3 rounded-lg border border-red-200">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-red-800">Block #{index + 1}</span>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-100"
                          onClick={() => {
                            const newBlockedDates = formData.blocked_dates.filter((_, i) => i !== index);
                            setFormData({...formData, blocked_dates: newBlockedDates});
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                      
                      <div className="grid md:grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs">From Date</Label>
                          <Input 
                            type="date" 
                            value={blocked.from_date}
                            min={getTodayDate()}
                            className="block w-full"
                            onChange={(e) => {
                              const newBlockedDates = [...formData.blocked_dates];
                              newBlockedDates[index].from_date = e.target.value;
                              setFormData({...formData, blocked_dates: newBlockedDates});
                            }}
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-xs">To Date (Optional)</Label>
                          <Input 
                            type="date" 
                            value={blocked.to_date}
                            min={blocked.from_date || getTodayDate()}
                            className="block w-full"
                            onChange={(e) => {
                              const newBlockedDates = [...formData.blocked_dates];
                              newBlockedDates[index].to_date = e.target.value;
                              setFormData({...formData, blocked_dates: newBlockedDates});
                            }}
                            placeholder="Leave empty for single day"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Reason</Label>
                          <Select 
                            value={blocked.reason || 'maintenance'}
                            onValueChange={(value) => {
                              const newBlockedDates = [...formData.blocked_dates];
                              newBlockedDates[index].reason = value;
                              setFormData({...formData, blocked_dates: newBlockedDates});
                            }}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select reason" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="maintenance">Maintenance</SelectItem>
                              <SelectItem value="reserved">Reserved</SelectItem>
                              <SelectItem value="unavailable">Unavailable</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <p className="text-xs text-red-600 mt-2">
                        {blocked.to_date 
                          ? `Bookings will be disabled from ${formatDateForDisplay(blocked.from_date)} to ${formatDateForDisplay(blocked.to_date)}`
                          : `Bookings will be disabled on ${formatDateForDisplay(blocked.from_date)}`
                        }
                      </p>
                    </div>
                  ))}
                </div>
              )}
              
              {(!formData.blocked_dates || formData.blocked_dates.length === 0) && (
                <p className="text-sm text-gray-500 italic">No dates blocked. Click "Add Blocked Period" to disable booking for specific dates.</p>
              )}
            </div>
            
            <div className="border-t pt-4">
              <h3 className="font-semibold text-md mb-4 text-[#2C3E50]">Default Pricing</h3>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label>Default Price per Night (₹)</Label>
                  <Input 
                    type="number" 
                    value={formData.default_price_per_night} 
                    onChange={(e) => setFormData({...formData, default_price_per_night: e.target.value})} 
                    placeholder="Base room price"
                  />
                </div>
                <div>
                  <Label>Price per Adult (₹)</Label>
                  <Input 
                    type="number" 
                    value={formData.default_price_per_adult} 
                    onChange={(e) => setFormData({...formData, default_price_per_adult: e.target.value})} 
                    placeholder="Extra charge per adult"
                  />
                </div>
                <div>
                  <Label>Price per Children (₹)</Label>
                  <Input 
                    type="number" 
                    value={formData.default_price_per_children} 
                    onChange={(e) => setFormData({...formData, default_price_per_children: e.target.value})} 
                    placeholder="Extra charge per child"
                  />
                </div>
              </div>
            </div>
            
            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-md text-[#2C3E50]">Seasonal Pricing</h3>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setFormData({
                      ...formData, 
                      seasonal_pricing: [
                        ...formData.seasonal_pricing, 
                        { from_date: '', to_date: '', price_per_night: '', price_per_adult: '', price_per_children: '', description: '' }
                      ]
                    });
                  }}
                >
                  Add Seasonal Period
                </Button>
              </div>
              
              {formData.seasonal_pricing && formData.seasonal_pricing.length > 0 && (
                <div className="space-y-4">
                  {formData.seasonal_pricing.map((seasonal, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg space-y-3">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-600">Period {index + 1}</span>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => {
                            const newSeasonalPricing = formData.seasonal_pricing.filter((_, i) => i !== index);
                            setFormData({...formData, seasonal_pricing: newSeasonalPricing});
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">From Date</Label>
                          <Input 
                            type="date" 
                            value={seasonal.from_date}
                            min={getTodayDate()}
                            className="block w-full"
                            onChange={(e) => {
                              const newSeasonalPricing = [...formData.seasonal_pricing];
                              newSeasonalPricing[index].from_date = e.target.value;
                              setFormData({...formData, seasonal_pricing: newSeasonalPricing});
                            }}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">To Date</Label>
                          <Input 
                            type="date" 
                            value={seasonal.to_date}
                            min={seasonal.from_date || getTodayDate()}
                            className="block w-full"
                            onChange={(e) => {
                              const newSeasonalPricing = [...formData.seasonal_pricing];
                              newSeasonalPricing[index].to_date = e.target.value;
                              setFormData({...formData, seasonal_pricing: newSeasonalPricing});
                            }}
                          />
                        </div>
                      </div>
                      
                      <div className="grid md:grid-cols-3 gap-3">
                        <div>
                          <Label className="text-xs">Price per Night (₹)</Label>
                          <Input 
                            type="number" 
                            value={seasonal.price_per_night} 
                            onChange={(e) => {
                              const newSeasonalPricing = [...formData.seasonal_pricing];
                              newSeasonalPricing[index].price_per_night = e.target.value;
                              setFormData({...formData, seasonal_pricing: newSeasonalPricing});
                            }}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Price per Adult (₹)</Label>
                          <Input 
                            type="number" 
                            value={seasonal.price_per_adult} 
                            onChange={(e) => {
                              const newSeasonalPricing = [...formData.seasonal_pricing];
                              newSeasonalPricing[index].price_per_adult = e.target.value;
                              setFormData({...formData, seasonal_pricing: newSeasonalPricing});
                            }}
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Price per Children (₹)</Label>
                          <Input 
                            type="number" 
                            value={seasonal.price_per_children} 
                            onChange={(e) => {
                              const newSeasonalPricing = [...formData.seasonal_pricing];
                              newSeasonalPricing[index].price_per_children = e.target.value;
                              setFormData({...formData, seasonal_pricing: newSeasonalPricing});
                            }}
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-xs">Description (e.g., "Pongal Festival")</Label>
                        <Input 
                          value={seasonal.description} 
                          onChange={(e) => {
                            const newSeasonalPricing = [...formData.seasonal_pricing];
                            newSeasonalPricing[index].description = e.target.value;
                            setFormData({...formData, seasonal_pricing: newSeasonalPricing});
                          }}
                          placeholder="Festival or event name"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {(!formData.seasonal_pricing || formData.seasonal_pricing.length === 0) && (
                <p className="text-sm text-gray-500 italic">No seasonal pricing periods added. Click "Add Seasonal Period" to create special rates for festivals or peak seasons.</p>
              )}
            </div>
            <div className="flex gap-4">
              <Button type="submit" className="btn-primary">Save</Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Price/Night</TableHead>
              <TableHead>Max Guests</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rooms.map((room) => (
              <TableRow key={room.id}>
                <TableCell className="font-medium">{room.name}</TableCell>
                <TableCell>₹{room.price_per_night?.toLocaleString()}</TableCell>
                <TableCell>{room.max_guests}</TableCell>
                <TableCell>
                  <span className={`px-2 py-1 rounded text-xs ${room.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                    {room.is_active ? "Active" : "Inactive"}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => { 
                      setEditingRoom(room); 
                      setFormData(prepareRoomDataForEdit(room)); 
                      setShowForm(true); 
                    }}>
                      Edit
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => deleteRoom(room.id)}>
                      Delete
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// FAQs Management
const FAQsManagement = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [formData, setFormData] = useState({ question: "", answer: "", order: 0 });

  useEffect(() => { fetchFaqs(); }, []);

  const fetchFaqs = async () => {
    try {
      const res = await axios.get(`${API}/admin/faqs`, getAuthHeaders());
      setFaqs(res.data);
    } catch (error) { toast.error("Failed to load FAQs"); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingFaq) {
        await axios.put(`${API}/admin/faqs/${editingFaq.id}`, formData, getAuthHeaders());
      } else {
        await axios.post(`${API}/admin/faqs`, formData, getAuthHeaders());
      }
      toast.success("FAQ saved");
      setShowForm(false);
      setEditingFaq(null);
      setFormData({ question: "", answer: "", order: 0 });
      fetchFaqs();
    } catch (error) { toast.error("Failed to save FAQ"); }
  };

  const deleteFaq = async (id) => {
    if (!window.confirm("Delete this FAQ?")) return;
    try {
      await axios.delete(`${API}/admin/faqs/${id}`, getAuthHeaders());
      toast.success("FAQ deleted");
      fetchFaqs();
    } catch (error) { toast.error("Failed to delete FAQ"); }
  };

  if (loading) return <div className="spinner mx-auto mt-20" />;

  return (
    <div data-testid="faqs-management">
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-serif text-3xl font-bold text-[#2C3E50]">FAQs</h1>
        <Button onClick={() => setShowForm(true)} className="btn-primary">Add FAQ</Button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Question</Label>
              <Input value={formData.question} onChange={(e) => setFormData({...formData, question: e.target.value})} required />
            </div>
            <div>
              <Label>Answer</Label>
              <Textarea value={formData.answer} onChange={(e) => setFormData({...formData, answer: e.target.value})} required />
            </div>
            <div>
              <Label>Order</Label>
              <Input type="number" value={formData.order} onChange={(e) => setFormData({...formData, order: parseInt(e.target.value)})} />
            </div>
            <div className="flex gap-4">
              <Button type="submit" className="btn-primary">Save</Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      <div className="space-y-4">
        {faqs.map((faq) => (
          <div key={faq.id} className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-medium text-[#2C3E50]">{faq.question}</h3>
                <p className="text-[#7F8C8D] mt-2">{faq.answer}</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => { setEditingFaq(faq); setFormData(faq); setShowForm(true); }}>Edit</Button>
                <Button size="sm" variant="destructive" onClick={() => deleteFaq(faq.id)}>Delete</Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Gallery Management
const GalleryManagement = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ caption: "", order: 0 });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);

  useEffect(() => { fetchGallery(); }, []);

  const fetchGallery = async () => {
    try {
      const res = await axios.get(`${API}/admin/gallery`, getAuthHeaders());
      console.log('Gallery raw response:', res.data);
      console.log('BACKEND_URL:', BACKEND_URL);
      // Process images to handle local uploads
      const processedImages = res.data.map(img => {
        const processedUrl = img.url.startsWith('/uploads/') ? `${BACKEND_URL}${img.url}` : img.url;
        console.log('Processing image:', img.url, '→', processedUrl);
        return {
          ...img,
          url: processedUrl
        };
      });
      console.log('Processed images:', processedImages);
      setImages(processedImages);
    } catch (error) { toast.error("Failed to load gallery"); }
    finally { setLoading(false); }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      toast.error("Please select an image file");
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('image', selectedFile);
      formDataToSend.append('caption', formData.caption);
      formDataToSend.append('order', formData.order);

      // Don't manually set Content-Type - let browser set it with correct boundary
      const config = getAuthHeaders();
      
      await axios.post(`${API}/admin/gallery`, formDataToSend, config);
      
      toast.success("Image added");
      setShowForm(false);
      setFormData({ caption: "", order: 0 });
      setSelectedFile(null);
      setPreviewUrl(null);
      fetchGallery();
    } catch (error) { 
      toast.error(error.response?.data?.error || "Failed to add image"); 
    }
  };

  const handleDeleteClick = (image) => {
    setImageToDelete(image);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!imageToDelete) return;
    try {
      await axios.delete(`${API}/admin/gallery/${imageToDelete.id}`, getAuthHeaders());
      toast.success("Image deleted");
      setShowDeleteModal(false);
      setImageToDelete(null);
      fetchGallery();
    } catch (error) { 
      toast.error("Failed to delete image"); 
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setImageToDelete(null);
  };

  if (loading) return <div className="spinner mx-auto mt-20" />;

  return (
    <div data-testid="gallery-management">
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-serif text-3xl font-bold text-[#2C3E50]">Gallery</h1>
        <Button onClick={() => setShowForm(true)} className="btn-primary">Add Image</Button>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={cancelDelete}>
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-[#2C3E50] mb-4">Delete this image?</h2>
            {imageToDelete && (
              <div className="mb-6">
                <img 
                  src={imageToDelete.url} 
                  alt={imageToDelete.caption} 
                  className="w-full h-48 object-cover rounded-lg border-2 border-gray-200" 
                />
                {imageToDelete.caption && (
                  <p className="text-sm text-[#7F8C8D] mt-2 text-center">{imageToDelete.caption}</p>
                )}
              </div>
            )}
            <p className="text-[#7F8C8D] mb-6">This action cannot be undone. The image will be permanently deleted.</p>
            <div className="flex gap-4 justify-end">
              <Button 
                variant="outline" 
                onClick={cancelDelete}
                className="px-6"
              >
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={confirmDelete}
                className="px-6"
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Image File</Label>
              <Input 
                type="file" 
                accept="image/*" 
                onChange={handleFileChange} 
                required 
              />
              {previewUrl && (
                <div className="mt-4">
                  <img 
                    src={previewUrl} 
                    alt="Preview" 
                    className="w-full max-w-md h-48 object-cover rounded-lg border-2 border-[#E67E22]" 
                  />
                </div>
              )}
            </div>
            <div>
              <Label>Caption</Label>
              <Input value={formData.caption} onChange={(e) => setFormData({...formData, caption: e.target.value})} />
            </div>
            <div className="flex gap-4">
              <Button type="submit" className="btn-primary">Save</Button>
              <Button type="button" variant="outline" onClick={() => {
                setShowForm(false);
                setSelectedFile(null);
                setPreviewUrl(null);
              }}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {images.map((image) => (
          <div key={image.id} className="relative group">
            <img 
              src={image.url} 
              alt={image.caption} 
              className="w-full h-40 object-cover rounded-lg" 
              onError={(e) => {
                console.error('Image failed to load:', image.url);
                e.target.style.border = '2px solid red';
              }}
              onLoad={() => console.log('Image loaded successfully:', image.url)}
            />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
              <Button size="sm" variant="destructive" onClick={() => handleDeleteClick(image)}>Delete</Button>
            </div>
            {/* <p className="text-xs text-gray-500 mt-1 truncate">{image.url}</p> */}
          </div>
        ))}
      </div>
    </div>
  );
};

// Messages Management
const MessagesManagement = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);

  useEffect(() => { fetchMessages(); }, []);

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/admin/contact-messages`, getAuthHeaders());
      setMessages(res.data);
    } catch (error) { toast.error("Failed to load messages"); }
    finally { setLoading(false); }
  };

  const markRead = async (id) => {
    try {
      await axios.put(`${API}/admin/contact-messages/${id}/read`, {}, getAuthHeaders());
      fetchMessages();
    } catch (error) { toast.error("Failed to update"); }
  };

  const handleDeleteClick = (msg) => {
    setMessageToDelete(msg);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!messageToDelete) return;
    try {
      await axios.delete(`${API}/admin/contact-messages/${messageToDelete.id}`, getAuthHeaders());
      toast.success("Message deleted");
      setShowDeleteModal(false);
      setMessageToDelete(null);
      fetchMessages();
    } catch (error) { 
      toast.error("Failed to delete message"); 
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setMessageToDelete(null);
  };

  if (loading) return <div className="spinner mx-auto mt-20" />;

  return (
    <div data-testid="messages-management">
      <h1 className="font-serif text-3xl font-bold text-[#2C3E50] mb-8">Contact Messages</h1>
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={cancelDelete}>
          <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-[#2C3E50] mb-4">Delete this message?</h2>
            <p className="text-[#7F8C8D] mb-2">
              <strong>{messageToDelete?.firstName} {messageToDelete?.lastName}</strong>
            </p>
            <p className="text-sm text-[#7F8C8D] mb-6 line-clamp-2">{messageToDelete?.message}</p>
            <div className="flex gap-4 justify-end">
              <Button 
                variant="outline" 
                onClick={cancelDelete}
                className="px-6"
              >
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={confirmDelete}
                className="px-6"
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {messages.map((msg) => (
          <div key={msg.id} className={`bg-white rounded-xl shadow-sm p-6 ${!msg.isRead ? "border-l-4 border-[#E67E22]" : ""}`}>
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-medium text-[#2C3E50]">{msg.firstName} {msg.lastName}</h3>
                <p className="text-sm text-[#7F8C8D]">{msg.email} • {msg.phone}</p>
                <p className="mt-2">{msg.message}</p>
                <p className="text-xs text-[#7F8C8D] mt-2">{new Date(msg.createdAt).toLocaleString()}</p>
              </div>
              <div className="flex gap-2">
                {!msg.isRead && (
                  <Button size="sm" variant="outline" onClick={() => markRead(msg.id)}>Mark Read</Button>
                )}
                <Button size="sm" variant="destructive" onClick={() => handleDeleteClick(msg)}>Delete</Button>
              </div>
            </div>
          </div>
        ))}
        {messages.length === 0 && <p className="text-center text-gray-500">No messages yet</p>}
      </div>
    </div>
  );
};

// Discount Codes Management
const DiscountCodesManagement = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [formData, setFormData] = useState({
    code: "",
    discount_type: "percentage",
    discount_value: "",
    min_booking_amount: 0,
    max_discount: "",
    valid_from: "",
    valid_until: "",
    usage_limit: 0,
    is_active: true
  });

  // Format date to DD-MM-YYYY
  const formatDateDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  useEffect(() => { fetchCodes(); }, []);

  const fetchCodes = async () => {
    try {
      const res = await axios.get(`${API}/admin/discount-codes`, getAuthHeaders());
      setCodes(res.data);
    } catch (error) { toast.error("Failed to load discount codes"); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        discount_value: parseFloat(formData.discount_value),
        min_booking_amount: parseFloat(formData.min_booking_amount) || 0,
        max_discount: formData.max_discount ? parseFloat(formData.max_discount) : null,
        usage_limit: parseInt(formData.usage_limit) || 0
      };
      
      if (editingCode) {
        await axios.put(`${API}/admin/discount-codes/${editingCode.id}`, data, getAuthHeaders());
        toast.success("Discount code updated");
      } else {
        await axios.post(`${API}/admin/discount-codes`, data, getAuthHeaders());
        toast.success("Discount code created");
      }
      setShowForm(false);
      setEditingCode(null);
      setFormData({
        code: "", discount_type: "percentage", discount_value: "",
        min_booking_amount: 0, max_discount: "", valid_from: "",
        valid_until: "", usage_limit: 0, is_active: true
      });
      fetchCodes();
    } catch (error) { 
      toast.error(error.response?.data?.detail || "Failed to save discount code"); 
    }
  };

  const deleteCode = async (id) => {
    if (!window.confirm("Delete this discount code?")) return;
    try {
      await axios.delete(`${API}/admin/discount-codes/${id}`, getAuthHeaders());
      toast.success("Discount code deleted");
      fetchCodes();
    } catch (error) { toast.error("Failed to delete discount code"); }
  };

  const toggleActive = async (code) => {
    try {
      await axios.put(`${API}/admin/discount-codes/${code.id}`, 
        { is_active: !code.is_active }, getAuthHeaders());
      toast.success(`Discount code ${!code.is_active ? "activated" : "deactivated"}`);
      fetchCodes();
    } catch (error) { toast.error("Failed to update"); }
  };

  if (loading) return <div className="spinner mx-auto mt-20" />;

  return (
    <div data-testid="discount-codes-management">
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-serif text-3xl font-bold text-[#2C3E50]">Discount Codes</h1>
        <Button onClick={() => { setShowForm(true); setEditingCode(null); }} className="btn-primary" data-testid="add-discount-btn">
          <Tag size={18} className="mr-2" /> Add Discount Code
        </Button>
      </div>

      {showForm && (
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8" data-testid="discount-form">
          <h2 className="font-semibold text-lg mb-4">{editingCode ? "Edit Discount Code" : "Create Discount Code"}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Code <span className="text-red-500">*</span></Label>
                <Input 
                  value={formData.code} 
                  onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})} 
                  placeholder="e.g., SUMMER20"
                  required 
                  data-testid="discount-code-input"
                />
              </div>
              <div>
                <Label>Discount Type</Label>
                <Select value={formData.discount_type} onValueChange={(val) => setFormData({...formData, discount_type: val})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed Amount (₹)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>Discount Value <span className="text-red-500">*</span></Label>
                <Input 
                  type="number" 
                  value={formData.discount_value} 
                  onChange={(e) => setFormData({...formData, discount_value: e.target.value})}
                  placeholder={formData.discount_type === "percentage" ? "e.g., 20" : "e.g., 500"}
                  required
                  data-testid="discount-value-input"
                />
              </div>
              <div>
                <Label>Min. Booking Amount (₹)</Label>
                <Input 
                  type="number" 
                  value={formData.min_booking_amount} 
                  onChange={(e) => setFormData({...formData, min_booking_amount: e.target.value})}
                  placeholder="0"
                />
              </div>
              <div>
                <Label>Max Discount (₹)</Label>
                <Input 
                  type="number" 
                  value={formData.max_discount} 
                  onChange={(e) => setFormData({...formData, max_discount: e.target.value})}
                  placeholder="No limit"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>Valid From <span className="text-red-500">*</span></Label>
                <Input 
                  type="date" 
                  value={formData.valid_from} 
                  onChange={(e) => setFormData({...formData, valid_from: e.target.value})}
                  required
                  data-testid="valid-from-input"
                />
              </div>
              <div>
                <Label>Valid Until <span className="text-red-500">*</span></Label>
                <Input 
                  type="date" 
                  value={formData.valid_until} 
                  onChange={(e) => setFormData({...formData, valid_until: e.target.value})}
                  required
                  data-testid="valid-until-input"
                />
              </div>
              <div>
                <Label>Usage Limit (0 = unlimited)</Label>
                <Input 
                  type="number" 
                  value={formData.usage_limit} 
                  onChange={(e) => setFormData({...formData, usage_limit: e.target.value})}
                  placeholder="0"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button type="submit" className="btn-primary" data-testid="save-discount-btn">Save</Button>
              <Button type="button" variant="outline" onClick={() => { setShowForm(false); setEditingCode(null); }}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Discount</TableHead>
              <TableHead>Min Amount</TableHead>
              <TableHead>Valid Period</TableHead>
              <TableHead>Usage</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {codes.map((code) => (
              <TableRow key={code.id}>
                <TableCell className="font-bold text-[#E67E22]">{code.code}</TableCell>
                <TableCell>
                  {code.discount_type === "percentage" 
                    ? `${code.discount_value}%${code.max_discount ? ` (max ₹${code.max_discount})` : ""}`
                    : `₹${code.discount_value}`
                  }
                </TableCell>
                <TableCell>₹{code.min_booking_amount?.toLocaleString() || 0}</TableCell>
                <TableCell className="text-sm">
                  {formatDateDisplay(code.valid_from)} to {formatDateDisplay(code.valid_until)}
                </TableCell>
                <TableCell>
                  {code.times_used || 0}{code.usage_limit > 0 ? `/${code.usage_limit}` : "/∞"}
                </TableCell>
                <TableCell>
                  <span className={`px-2 py-1 rounded text-xs ${code.is_active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                    {code.is_active ? "Active" : "Inactive"}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => toggleActive(code)}>
                      {code.is_active ? "Disable" : "Enable"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { 
                      setEditingCode(code); 
                      setFormData({
                        code: code.code,
                        discount_type: code.discount_type,
                        discount_value: code.discount_value,
                        min_booking_amount: code.min_booking_amount || 0,
                        max_discount: code.max_discount || "",
                        valid_from: code.valid_from,
                        valid_until: code.valid_until,
                        usage_limit: code.usage_limit || 0,
                        is_active: code.is_active
                      }); 
                      setShowForm(true); 
                    }}>
                      Edit
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => deleteCode(code.id)}>
                      Delete
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {codes.length === 0 && (
          <p className="text-center py-8 text-gray-500">No discount codes yet. Create one to get started!</p>
        )}
      </div>
    </div>
  );
};

// Main Admin Dashboard
export default function AdminDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminEmail");
    localStorage.removeItem("adminName");
    navigate("/admin/login");
  };

  const menuItems = [
    { name: "Dashboard", path: "/admin/dashboard", icon: LayoutDashboard },
    { name: "Bookings", path: "/admin/bookings", icon: CalendarDays },
    { name: "Rooms", path: "/admin/rooms", icon: Home },
    { name: "Discount Codes", path: "/admin/discounts", icon: Percent },
    { name: "Gallery", path: "/admin/gallery", icon: Image },
    { name: "FAQs", path: "/admin/faqs", icon: HelpCircle },
    { name: "Messages", path: "/admin/messages", icon: MessageSquare },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#F5F7FA]" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#2C3E50] transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 transition-transform duration-300`}>
        <div className="flex items-center justify-between h-20 px-6 border-b border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="bg-white rounded-lg p-2">
              <img 
                src="/assets/logo.png" 
                alt="Wyldwoods" 
                className="h-12"
              />
            </div>
          </div>
          <button className="lg:hidden text-white" onClick={() => setSidebarOpen(false)}>
            <X size={24} />
          </button>
        </div>
        <nav className="mt-6">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center px-6 py-3 text-gray-300 hover:bg-[#34495E] transition-colors ${isActive(item.path) ? "bg-[#E67E22] text-white" : ""}`}
              onClick={() => setSidebarOpen(false)}
            >
              <item.icon size={20} className="mr-3" />
              {item.name}
            </Link>
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <button
            onClick={handleLogout}
            className="flex items-center text-gray-300 hover:text-white transition-colors w-full"
            data-testid="logout-btn"
          >
            <LogOut size={20} className="mr-3" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top bar */}
        <header className="bg-white shadow-sm h-20 flex items-center justify-between px-6">
          <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
            <Menu size={24} />
          </button>
          <div className="flex items-center space-x-4">
            <span className="text-[#7F8C8D]">Welcome, {localStorage.getItem("adminName") || "Admin"}</span>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <Routes>
            <Route path="/dashboard" element={<DashboardHome />} />
            <Route path="/bookings" element={<BookingsManagement />} />
            <Route path="/rooms" element={<RoomsManagement />} />
            <Route path="/discounts" element={<DiscountCodesManagement />} />
            <Route path="/gallery" element={<GalleryManagement />} />
            <Route path="/faqs" element={<FAQsManagement />} />
            <Route path="/messages" element={<MessagesManagement />} />
            <Route path="*" element={<DashboardHome />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
